import { useState, useRef, useEffect } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

const navLinks = [
  { name: 'Features', href: '#features' },
  { name: 'Specialties', href: '#specialties' },
  { name: 'Pricing', href: '#pricing' },
  { name: 'Company', href: '/company.html' },
];

export default function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSolutionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMobileNavClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      setMobileOpen(false);
      window.setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 320);
    } else {
      setMobileOpen(false);
    }
  };

  return (
    <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <img src="/aura-logo.png" alt="PulseStack AI" className="h-7 w-auto" />
            <span className="text-xl font-bold text-gray-900">PulseStack <span className="text-primary-600">AI</span></span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {/* Solutions dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setSolutionsOpen(!solutionsOpen)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Solutions
                <svg
                  className={`h-3.5 w-3.5 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {solutionsOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                  <a href="/solutions/aura-chart.html" className="block px-4 py-3 hover:bg-gray-50 transition-colors rounded-lg mx-1">
                    <span className="block text-sm font-semibold text-gray-900">Aura-Chart</span>
                    <span className="block text-xs text-gray-400 mt-0.5">Clinical documentation</span>
                  </a>
                  <a href="/solutions/aura-triage.html" className="block px-4 py-3 hover:bg-gray-50 transition-colors rounded-lg mx-1">
                    <span className="block text-sm font-semibold text-gray-900">Aura-Triage</span>
                    <span className="block text-xs text-gray-400 mt-0.5">Fax triage & routing</span>
                  </a>
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://calendly.com/pulsestack1/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Book a Demo
            </a>
            <a
              href="https://chromewebstore.google.com/detail/aura-chart/llkaecoefncjopopkbkhcmajliikeani"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary-500 transition-colors whitespace-nowrap"
            >
              Try Free →
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-gray-500 hover:text-gray-900 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileOpen ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-4 border-t border-gray-100 space-y-1">
            <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-widest">Solutions</div>
            <a href="/solutions/aura-chart.html" className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm" onClick={() => setMobileOpen(false)}>
              Aura-Chart
            </a>
            <a href="/solutions/aura-triage.html" className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm" onClick={() => setMobileOpen(false)}>
              Aura-Triage
            </a>
            <hr className="border-gray-100 my-2" />
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg text-sm"
                onClick={(e) => handleMobileNavClick(e, link.href)}
              >
                {link.name}
              </a>
            ))}
            <div className="pt-2 pb-1 px-1 space-y-2">
              <a
                href="https://calendly.com/pulsestack1/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center py-3 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                onClick={() => setMobileOpen(false)}
              >
                Book a Demo
              </a>
              <a
                href="https://chromewebstore.google.com/detail/aura-chart/llkaecoefncjopopkbkhcmajliikeani"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center py-3 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-500"
                onClick={() => setMobileOpen(false)}
              >
                Try Free for 14 Days
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
